/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useAggregate } from 'hooks';
import { Config } from 'configs';
import { getFullDateInCustomTimezone, utcDateToMillis } from 'utils/dateManagement';
import { PageTitle } from 'components/layouts/pageTitle';
import { DataColumn, List, ListHeader } from 'components/lists';
import { extractRecordsFromAggreagte } from 'utils/renderingHelpers';
import { Pagination } from 'components/lists/pagination';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Input } from 'components/formInputs/input';
import { PageContent } from 'components/layouts/pageContent';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';

export const Reviews = () => {
  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterFromDate, setFilterFromDate, filterFromDateRef] = useLocalStorage(`reviews-filter-from-${auth.company?.company}`, '');
  const [filterToDate, setFilterToDate, filterToDateRef] = useLocalStorage(`reviews-filter-to-${auth.company?.company}`, '');
  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`reviews-filter-restaurant-${auth.company?.company}`, '');

  const [sort, setSort] = useLocalStorage(`reviews-sort-${auth.company?.company}`, { field: 'date', dbField: 'date', order: -1 });
  const changedSort = useRef({ value: null }).current;

  const navigate = useNavigate();

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.REVIEWS,
    aggregateId: Config.DATA.AGGREGATES.GET_REVIEWS,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { companyId: auth.company?.company };

      const fromDateMillis = utcDateToMillis(filterFromDateRef.value);
      const toDateMillis = utcDateToMillis(filterToDateRef.value ? `${filterToDateRef.value}T23:59:59Z` : null);

      if (fromDateMillis && toDateMillis) {
        filter.dateFilter = `{ $and: [{ "_meta.createdOn": { $gte: ${fromDateMillis} } }, { "_meta.createdOn": { $lte: ${toDateMillis} } } ]}`;
      } else if (fromDateMillis) {
        filter.dateFilter = `{ "_meta.createdOn": { $gte: ${fromDateMillis} } }`;
      } else if (toDateMillis) {
        filter.dateFilter = `{ "_meta.createdOn": { $lte: ${toDateMillis} } }`;
      } else {
        filter.dateFilter = {};
      }

      if (filterRestaurantRef.value) {
        filter.restaurantFilter = `{ restaurant: { $eq: ObjectId('${filterRestaurantRef.value}') } }`;
      } else {
        filter.restaurantFilter = {};
      }

      const sortKey = changedSort.value?.dbField || sort?.dbField || 'date';

      if (Array.isArray(sortKey)) {
        filter.sort = {};
        sortKey.forEach((key) => {
          filter.sort[`'${key}'`] = changedSort.value?.order || sort?.order || -1;
        });
      } else {
        filter.sort = { [`${sortKey}`]: changedSort.value?.order || sort?.order || -1 };
      }

      return filter;
    },
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    loadOnInit: true,
    aggregateIdV2: 'AdminListReviews',
    companyIdForV2: auth.company?.company,
  };

  const [fetchReviews, reviewsResponse, isLoading] = useAggregate(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onFilterReviews = () => {
    fetchReviews();
  };

  const onClearFilters = () => {
    setFilterFromDate('');
    setFilterToDate('');
    setFilterRestaurant('');
    onFilterReviews();
  };

  const onSortOrders = (field, dbField, order) => {
    setSort({ field, dbField, order });
    changedSort.value = { field, dbField, order };
    onFilterReviews();
  };

  const onEditClicked = (item) => {
    navigate(`/reviews/${item._id}`);
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  const { paginatedResults: reviews, totalCount } = extractRecordsFromAggreagte(reviewsResponse);

  return (
    <ListLayout isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Reviews" isTopLevel />

      <PageContent>
        <FilterBlock buttons={[
          {
            onPress: onClearFilters,
            text: 'Clear',
            class: 'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
          },
          {
            onPress: onFilterReviews,
            text: 'Search',
            class: 'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
            isLoading: !!isLoading,
          },
        ]}
        >
          <FilterField>
            <div>
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700">
                Date from
              </label>
              <div className="mt-1 relative text-sm text-gray-500">
                <Input
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="fromDate"
                  id="fromDate"
                  type="text"
                  value={filterFromDate}
                  onChange={(e) => {
                    setFilterFromDate(e.target.value);
                  }}
                  onEnterClicked={() => {
                    onFilterReviews();
                  }}
                  placeholder="YYYY-MM-DD"
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>
          </FilterField>
          <FilterField>
            <div>
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700">
                Date to
              </label>
              <div className="mt-1 relative text-sm text-gray-500">
                <Input
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="toDate"
                  id="toDate"
                  type="text"
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                  onEnterClicked={() => {
                    onFilterReviews();
                  }}
                  placeholder="YYYY-MM-DD"
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
              }}
              options={[{ label: 'All', value: '' }, ...(companyState.restaurants || []).map((restaurant) => ({
                label: restaurant.name,
                value: restaurant._id,
              }))]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        <ListHeader
          title="Overview"
          description={`Reviews of ${auth.company?.display_name}`}
        />

        <List source={reviews} sort={sort} onRowClick={onEditClicked}>
          <DataColumn
            name="Date"
            dbColumnName="date"
            render={(x) => {
              return getFullDateInCustomTimezone(x.date, x.timezone);
            }}
            headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            dataCellClass="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
            onSort={(sortOrder) => {
              onSortOrders('date', 'date', sortOrder);
            }}
          />
          <DataColumn
            name="Restaurant"
            dbColumnName="restaurnat_name"
            headerClass="hidden px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:table-cell"
            dataCellClass="hidden px-6 py-4 whitespace-nowrap text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Order"
            dbColumnName="order_number"
            render={(x) => (x.order_number ? `#${x.order_number}` : '')}
            headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
          />
          <DataColumn
            name="Rating"
            dbColumnName="rating"
            headerClass="hidden px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:table-cell"
            dataCellClass="hidden px-6 py-4 whitespace-nowrap text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Comment"
            dbColumnName="comment"
            render={(x) => {
              if (!x.comment) {
                return '';
              }

              const symbolsToShow = 30;

              if (x.comment.length < symbolsToShow) {
                return x.comment;
              }

              return `${x.comment.substring(0, symbolsToShow)}...`;
            }}
            headerClass="hidden px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:table-cell"
            dataCellClass="hidden px-6 py-4 whitespace-nowrap text-sm text-gray-500 sm:table-cell"
          />

          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              const request = { ...requestData, pageNumber: pageNumber - 1 };

              await fetchReviews(false, request);
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
